import { useEffect, useState } from 'react';
import {
  listQuerySets, saveQuerySetFor, deleteQuerySet, activateQuerySet,
  getRecoverableSets, restoreQuerySet, listSchedules, type QuerySet, type RecoverableSet, type Schedule,
} from '../account';
import SetSchedule from './SetSchedule';

/**
 * The question sets, per domain, owned by the client (D-33/D-34).
 *
 * A domain can hold several: a first generated proposal, the hand-written set
 * that replaced it, a narrower one for a new topic. Exactly one is ACTIVE and
 * that is what every run of the domain asks — reusing the same questions is the
 * only reason a trend means anything.
 *
 * Generating a proposal used to overwrite the hand-written set, which is the
 * opposite of the client owning it. A proposal is now just another set.
 */
export default function QuerySets({ domains, plan = 'free' }: { domains: string[]; plan?: 'free' | 'pro' }): JSX.Element {
  const [sets, setSets] = useState<QuerySet[] | null>(null);
  // Each set's own automatic re-check, fetched once for the whole list.
  const [schedules, setSchedules] = useState<Map<string, Schedule> | null>(null);
  const [editing, setEditing] = useState<{ id?: string; domain: string; name: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Sets this account measured with that are no longer saved. A report naming a
  // set you cannot run is a dead end, and one of these lists lost six of them
  // to a bug in the "generate new questions" path.
  const [lost, setLost] = useState<RecoverableSet[]>([]);

  const reload = (): void => {
    void listQuerySets().then((d) => setSets(d.sets)).catch(() => setSets([]));
    void getRecoverableSets().then(setLost).catch(() => setLost([]));
    void listSchedules()
      .then((rows) => setSchedules(new Map(rows.filter((r) => r.querySetId).map((r) => [r.querySetId!, r]))))
      .catch(() => setSchedules(new Map()));
  };
  useEffect(reload, []);

  const known = [...new Set([...(sets ?? []).map((s) => s.domain), ...domains])].sort();

  async function save(): Promise<void> {
    if (!editing || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const queries = editing.text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (queries.length === 0) { setMsg('A set needs at least one question.'); return; }
      const r = await saveQuerySetFor({
        id: editing.id ? Number(editing.id) : undefined,
        url: `https://${editing.domain}/`,
        name: editing.name.trim() || 'My questions',
        queries,
      });
      setMsg(r.dropped > 0
        ? `Saved ${r.set.queries.length}, ${r.dropped} blank or duplicate line${r.dropped === 1 ? '' : 's'} dropped.`
        : `Saved ${r.set.queries.length} questions.`);
      setEditing(null);
      reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'could not save');
    } finally { setBusy(false); }
  }

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try { await fn(); reload(); } finally { setBusy(false); }
  }

  if (!sets) return <></>;
  const lines = editing ? editing.text.split('\n').filter((l) => l.trim()).length : 0;
  const field = 'bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-3 py-1.5 text-sm text-text-primary outline-none';

  return (
    <div className="mb-6 border border-warm-dark-2 rounded-xl p-4">
      {/* No heading here: this panel IS a dashboard page now, and the page
          head above it already says what this is. Two titles in a row read
          as a mistake, because they are one. */}
      <p className="text-sm text-text-secondary mb-2">
        The questions put to AI about each domain. Keep several, one per product line, market or language, and each one
        keeps its own trend and its own <span className="text-text-primary">automatic re-check</span>. The{' '}
        <span className="text-text-primary">active</span> set is the one a new scan offers first.
      </p>
      <p className="text-sm text-text-secondary mb-3">
        <span className="text-text-primary">Write these yourself.</span> The generator reads your pages and is a decent
        starting point, but it cannot know your market&apos;s own wording, and it cannot tell which ground you genuinely
        own, on a real brand it missed seven of the eight questions that had ever earned a mention, and spent five on
        platforms that brand does not serve. Those five cost the same to ask as the ones that could have won.
      </p>

      {lost.length > 0 && (
        <div className="mb-4 border border-[#B7791F]/40 bg-[#B7791F]/5 rounded-lg p-4">
          <h3 className="font-mono text-sm uppercase tracking-widest text-[#E8B04B] mb-1">
            Measured with, but no longer saved
          </h3>
          <p className="text-sm text-text-secondary mb-3 max-w-prose">
            Your history shows runs that asked {lost.length === 1 ? 'this set' : 'these sets'}, but {lost.length === 1 ? 'it is' : 'they are'} not
            in your saved sets any more. Every run records the questions it asked, so {lost.length === 1 ? 'it' : 'each'} can be
            rebuilt exactly. Restoring does not make it active, it just puts it back on the shelf.
          </p>
          <ul className="flex flex-col gap-2">
            {lost.map((r) => (
              <li key={`${r.domain}/${r.name}`} className="flex items-center gap-3 flex-wrap text-sm">
                <span className="font-mono text-text-primary">{r.name}</span>
                <span className="font-mono text-xs text-text-secondary">
                  {r.domain} · {r.queries.length} questions · used in {r.runs} {r.runs === 1 ? 'run' : 'runs'} ·
                  last {new Date(r.lastUsedAt).toLocaleDateString()}
                </span>
                <button
                  onClick={() => void act(async () => {
                    const out = await restoreQuerySet(r.name, r.domain);
                    setMsg(`Restored "${r.name}" with ${out.restored} questions.`);
                  })}
                  disabled={busy}
                  className="ml-auto font-mono text-xs text-cyan border border-cyan/40 rounded px-2 py-1 hover:bg-cyan/10 disabled:opacity-40 transition"
                >restore</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {known.length === 0 ? (
        <p className="font-mono text-sm text-text-secondary">Scan a domain first, then its sets appear here.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {known.map((d) => {
            const mine = sets.filter((s) => s.domain === d);
            return (
              <div key={d} className="border border-warm-dark-2 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <p className="text-text-primary">{d}</p>
                  <button
                    onClick={() => { setMsg(null); setEditing({ domain: d, name: 'My questions', text: '' }); }}
                    className="font-mono text-sm text-cyan hover:underline"
                  >
                    + new set
                  </button>
                </div>

                {mine.length === 0 ? (
                  <p className="font-mono text-xs text-text-secondary">No set yet, questions are generated on each run.</p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {mine.map((s) => (
                      <div key={s.id} className={`flex items-center justify-between gap-3 flex-wrap border rounded px-3 py-2 ${s.isActive ? 'border-success/40 bg-success/5' : 'border-warm-dark-2'}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-text-primary truncate">
                            {s.name}
                            {s.isActive && <span className="ml-2 font-mono text-[10px] uppercase tracking-wide text-success">active</span>}
                            {s.source === 'generated' && <span className="ml-2 font-mono text-[10px] uppercase tracking-wide text-text-secondary/70">AI proposal</span>}
                          </p>
                          <p className="font-mono text-xs text-text-secondary">
                            {s.queries.length} questions · updated {new Date(s.updatedAt).toLocaleDateString()}
                          </p>
                          {schedules && (
                            <div className="mt-1.5">
                              <SetSchedule set={s} plan={plan} initial={schedules.get(s.id) ?? null} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 font-mono text-sm">
                          {!s.isActive && (
                            <button onClick={() => void act(() => activateQuerySet(s.id))} disabled={busy}
                              className="text-cyan hover:underline disabled:opacity-40">use this</button>
                          )}
                          <button onClick={() => { setMsg(null); setEditing({ id: s.id, domain: d, name: s.name, text: s.queries.join('\n') }); }}
                            className="text-text-secondary hover:text-cyan">edit</button>
                          {/* Deleting a set breaks the trend it measured: later runs
                              cannot be compared against earlier ones without the
                              same questions. Worth a sentence and a click. */}
                          <button
                            onClick={() => {
                              if (!window.confirm(
                                `Delete "${s.name}" (${s.queries.length} questions)?\n\n`
                                + 'Runs already measured with it keep their results, but you will not be '
                                + 'able to run these questions again, so the trend stops here.\n\n'
                                + 'It can be rebuilt later from a run that used it.'
                              )) return;
                              void act(() => deleteQuerySet(s.id));
                            }}
                            disabled={busy}
                            className="text-text-secondary hover:text-[#E0574A] disabled:opacity-40">delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl bg-warm-dark border border-warm-dark-2 rounded-xl p-5 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-serif text-xl">{editing.id ? 'Edit set' : 'New set'}</h3>
                <p className="font-mono text-xs text-text-secondary">{editing.domain}</p>
              </div>
              <button onClick={() => setEditing(null)} className="font-mono text-sm text-text-secondary hover:text-cyan">✕ close</button>
            </div>

            <label className="flex flex-col gap-1 mb-3">
              <span className="font-mono text-xs text-text-secondary">Name</span>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="e.g. Fulfilment questions" className={field} />
            </label>

            <textarea
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
              rows={16}
              spellCheck={false}
              placeholder={'One question per line, in the language your buyers use.\n\nexact online koppelen met fulfilment\nlightspeed koppelen met fulfilment\nhoe koppel ik mijn webshop aan Exact Online én mijn fulfilmentpartner zonder maatwerk?'}
              className="w-full bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-3 py-2 text-sm text-text-primary outline-none font-mono leading-relaxed"
            />

            <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
              <p className="font-mono text-xs text-text-secondary">
                {lines} question{lines === 1 ? '' : 's'} · one per line · blanks and duplicates dropped
                {lines > 25 && <span className="text-estimated"> · over 25 costs more per run</span>}
              </p>
              <button onClick={() => void save()} disabled={busy}
                className="font-mono text-sm bg-cyan text-warm-dark rounded-lg px-4 py-2 disabled:opacity-50 hover:opacity-90 transition">
                {busy ? 'saving…' : editing.id ? 'Save changes' : 'Create set'}
              </button>
            </div>
            {msg && <p className="font-mono text-sm text-text-secondary mt-2">{msg}</p>}
            <p className="text-sm text-text-secondary mt-3">
              Anchor them on the one thing you genuinely own. A question about a platform you don&apos;t actually serve
              is a paid measurement of ground you can never win. Changing the questions restarts the trend, which is
              correct. It is a different measurement.
            </p>
          </div>
        </div>
      )}
      {msg && !editing && <p className="font-mono text-sm text-text-secondary mt-3">{msg}</p>}
    </div>
  );
}
